import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import useToast from "../../../../components/common/useToast";
import apiFunctions from "../../../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler, formatDateTime } from "../../../../utils/HelperFunctions";
import "../../../../assets/css/datatable.css";
import DeleteConfirmation from "../../../../components/modal-popup/delete-popup";
import { Skeleton } from "primereact/skeleton";
import { ArrowLeftIcon, PlusIcon, VideoCameraIcon } from "@heroicons/react/24/outline";
import { SECTION_OPTIONS, getSectionLabel } from "../../../../constants/sectionOptions";
import { VideoEmbed } from "../../../../components/homeBuilder/BuilderSections";

type PageVideoItem = {
    _id: string;
    page: string;
    link: string;
    isactive: boolean;
    createddate: string;
};

const PageVideoPage: React.FC = () => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [videoData, setVideoData] = useState<PageVideoItem[]>([]);
    const [dataLoad, setDataLoad] = useState(true);
    const [load, setLoad] = useState(true);
    const initialized = useRef(false);
    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState<any>(0);
    const { showToast }: any = useToast();
    const [name, setName] = useState<string>("");

    const [videoToDelete, setVideoToDelete] = useState<PageVideoItem | null>(null);
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);

    const [showFormDialog, setShowFormDialog] = useState(false);
    const [editingVideo, setEditingVideo] = useState<PageVideoItem | null>(null);
    const [formPage, setFormPage] = useState<string>("");
    const [formLink, setFormLink] = useState("");
    const [saving, setSaving] = useState(false);

    const startIndex = (currentPage - 1) * itemsPerPage;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    async function getPageVideoData() {
        try {
            setDataLoad(true);
            const params: any = {
                page: currentPage + 1,
                limit: itemsPerPage,
            };
            if (name) params.search = name;

            const response = await apiFunctions.get("/pagevideo", { params });

            if (response?.data?.success) {
                setVideoData(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response?.data?.message || "Failed to load page videos",
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setDataLoad(false);
            setLoad(false);
        }
    }

    const getsearchdata = (e: any) => {
        if (e.target.value.length >= 2) {
            setName(e.target.value);
            setCurrentPage(0);
        } else {
            setName("");
        }
    };

    useEffect(() => {
        if (!load) {
            getPageVideoData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, name]);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getPageVideoData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBack = () => {
        navigate('/admin/settings');
    };

    const openAddDialog = () => {
        setEditingVideo(null);
        setFormPage("");
        setFormLink("");
        setShowFormDialog(true);
    };

    const openEditDialog = (rowData: PageVideoItem) => {
        setEditingVideo(rowData);
        setFormPage(rowData.page || "");
        setFormLink(rowData.link || "");
        setShowFormDialog(true);
    };

    const handleFormSubmit = async () => {
        if (!formPage) {
            showToast({ severity: "warn", summary: "Warning", detail: "Please select a page", life: 3000 });
            return;
        }
        if (!formLink.trim()) {
            showToast({ severity: "warn", summary: "Warning", detail: "Please enter a link", life: 3000 });
            return;
        }
        if (!/^https?:\/\//i.test(formLink.trim())) {
            showToast({ severity: "warn", summary: "Warning", detail: "Link must start with http:// or https://", life: 3000 });
            return;
        }

        try {
            setSaving(true);
            const payload = { page: formPage, link: formLink.trim() };

            const response = editingVideo
                ? await apiFunctions.patch(`/pagevideo/${editingVideo._id}`, payload)
                : await apiFunctions.post("/pagevideo", payload);

            if (response?.data?.success) {
                showToast({
                    severity: "success",
                    summary: "Success",
                    detail: response.data.message,
                    life: 3000,
                });
                setShowFormDialog(false);
                getPageVideoData();
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response?.data?.message || "Something went wrong",
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (rowData: PageVideoItem, value: boolean) => {
        try {
            await apiFunctions.patch(`/pagevideo/${rowData._id}`, { isactive: value });
            getPageVideoData();
        } catch (err) {
            errorHandler(err as AxiosError);
        }
    };

    const handleDelete = (rowData: PageVideoItem) => {
        setVideoToDelete(rowData);
        setDeleteConfirmationVisible(true);
    };

    const hideDeleteConfirmation = () => {
        setDeleteConfirmationVisible(false);
        setVideoToDelete(null);
    };

    const confirmDelete = async () => {
        if (videoToDelete) {
            try {
                const response = await apiFunctions.delete(`/pagevideo/${videoToDelete._id}`);
                if (response?.data?.success) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Page video deleted successfully", life: 3000 });
                } else {
                    showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Delete failed", life: 3000 });
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setDeleteConfirmationVisible(false);
                setVideoToDelete(null);
                getPageVideoData();
            }
        }
    };

    const actionBodyTemplate = (rowData: PageVideoItem) => {
        return (
            <div className="action-icons flex items-center gap-2 flex-wrap">
                <Button label="Edit" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => openEditDialog(rowData)} />
                <Button label="Delete" outlined size="small" style={{ color: "#dc2626", borderColor: "#dc2626" }} onClick={() => handleDelete(rowData)} />
            </div>
        );
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
            ];

            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries-text mx-1">entries</span>;
        },
    };

    const header = (
        <div className="grid grid-cols-12 gap-4 items-center table-header">
            <div className="col-span-12 sm:col-span-6">
                <h2 className="card-title m-0 text-lg sm:text-xl">Page Videos</h2>
            </div>
            <div className="col-span-12 sm:col-span-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-gray-600">Search:</span>
                    <PrimeInputText
                        type="text"
                        className="search-input w-full sm:w-auto"
                        placeholder="Search by page or link..."
                        onChange={(e) => getsearchdata(e)}
                    />
                </div>
                <div className="round-btn-div">
                    <Button
                        icon={<PlusIcon className="w-6 h-6" />}
                        className="round-button"
                        onClick={openAddDialog}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className="p-4 standalone-page-bg">
            <div className="flex items-center gap-2 mb-2 cursor-pointer w-max" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" />
                <h3 className="m-0 text-lg sm:text-xl">Back</h3>
            </div>
            <div className="table-margin inner-table-margin">
                {isMobile ? (
                    <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                        <div className="p-3 pb-2">{header}</div>
                        <div className="flex flex-col gap-2 px-3 pb-3">
                            {dataLoad ? (
                                Array.from({ length: itemsPerPage }, (_, i) => (
                                    <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <Skeleton width="60%" height="1rem" className="mb-2" />
                                        <Skeleton width="40%" height="0.8rem" />
                                    </div>
                                ))
                            ) : videoData.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "64px 0" }}>
                                    <VideoCameraIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                    <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No page videos found</p>
                                </div>
                            ) : (
                                videoData.map((rowData) => (
                                    <div key={rowData._id} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <p className="m-0 font-semibold text-sm break-words" style={{ color: "#264164" }}>{getSectionLabel(rowData.page)}</p>
                                        <div className="mt-2" style={{ width: "100%", maxWidth: "320px" }}>
                                            <VideoEmbed link={rowData.link} title={getSectionLabel(rowData.page)} />
                                        </div>
                                        <div className="flex items-center gap-2 mt-2">
                                            {/* shrink-0: without it, a flex row can squeeze the
                                                switch's own width down to fit its neighboring text
                                                on a narrow screen instead of wrapping the text,
                                                rendering the slider/knob squashed. */}
                                            <InputSwitch checked={rowData.isactive} onChange={(e) => handleToggleActive(rowData, e.value)} className="shrink-0" />
                                            <span className="text-xs" style={{ color: "#4b5563" }}>{rowData.isactive ? "Active" : "Inactive"}</span>
                                        </div>
                                        <div className="flex justify-end gap-2 mt-2 flex-wrap">
                                            {actionBodyTemplate(rowData)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ) : (
                    <DataTable
                        scrollable
                        scrollHeight="calc(100vh - 215px)"
                        style={{ minHeight: "calc(100vh - 140px)", background: "#fff" }}
                        header={header}
                        value={
                            dataLoad
                                ? Array.from({ length: itemsPerPage }, (_, i) => ({ _id: `_skeleton_${i}` }))
                                : videoData
                        }
                        resizableColumns
                        rows={itemsPerPage}
                        emptyMessage={
                            <div style={{ textAlign: "center", padding: "64px 0" }}>
                                <VideoCameraIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No page videos found</p>
                            </div>
                        }
                    >
                        <Column field="page" header="Page" body={(rowData) => dataLoad ? <Skeleton /> : getSectionLabel(rowData.page)} style={{ minWidth: "150px" }} />
                        <Column
                            field="link"
                            header="Preview"
                            style={{ minWidth: "280px" }}
                            body={(rowData) => dataLoad ? <Skeleton height="140px" /> : (
                                <div style={{ width: "240px" }}>
                                    <VideoEmbed link={rowData.link} title={getSectionLabel(rowData.page)} />
                                </div>
                            )}
                        />
                        <Column
                            header="Status"
                            style={{ minWidth: "120px" }}
                            body={(rowData) => dataLoad ? <Skeleton /> : (
                                <InputSwitch checked={rowData.isactive} onChange={(e) => handleToggleActive(rowData, e.value)} className="shrink-0" />
                            )}
                        />
                        <Column
                            field="createddate"
                            header="Created Date"
                            style={{ minWidth: "180px" }}
                            body={(rowData) => dataLoad ? <Skeleton /> : formatDateTime(rowData.createddate)}
                        />
                        <Column header="Action" className="action-column" frozen alignFrozen="right" style={{ minWidth: "200px" }} body={(rowData) => dataLoad ? <Skeleton /> : actionBodyTemplate(rowData)} />
                    </DataTable>
                )}
                {totalRecords > 0 && <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                    <Paginator
                        template={paginatoreTemplate}
                        first={startIndex}
                        rows={itemsPerPage}
                        totalRecords={totalRecords}
                    />
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage === 0}
                        >
                            <span className="sm:hidden">Prev</span>
                            <span className="hidden sm:inline">Previous</span>
                        </Button>

                        <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                            <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                Page {currentPage + 1} of {totalPages || 1}
                            </span>
                        </Button>

                        <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                        >
                            Next
                        </Button>
                    </div>
                </div>}
            </div>

            <Dialog
                header={editingVideo ? "Edit Page Video" : "Add Page Video"}
                visible={showFormDialog}
                style={{ width: "90vw", maxWidth: "480px" }}
                modal
                draggable={false}
                onHide={() => setShowFormDialog(false)}
            >
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                        <label className="font-semibold text-sm">Page</label>
                        <Dropdown
                            value={formPage}
                            options={SECTION_OPTIONS}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => setFormPage(e.value)}
                            placeholder="Select a page"
                            className="w-full"
                            filter
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="font-semibold text-sm">Link</label>
                        <PrimeInputText
                            type="text"
                            value={formLink}
                            onChange={(e) => setFormLink(e.target.value)}
                            placeholder="https://..."
                            className="w-full"
                        />
                    </div>
                    <div className="flex justify-end gap-2 mt-3">
                        <Button
                            label="Cancel"
                            severity="secondary"
                            onClick={() => setShowFormDialog(false)}
                            disabled={saving}
                        />
                        <Button
                            label={editingVideo ? "Update" : "Add"}
                            loading={saving}
                            onClick={handleFormSubmit}
                        />
                    </div>
                </div>
            </Dialog>

            <DeleteConfirmation
                visible={deleteConfirmationVisible}
                onHide={hideDeleteConfirmation}
                onDelete={confirmDelete}
                header="Delete Page Video"
                message={`Are you sure you want to delete the video for "${getSectionLabel(videoToDelete?.page || "")}"? This action cannot be undone.`}
            />
        </div>
    );
};

export default PageVideoPage;
